import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiError, fcfa } from "../lib/api";
import { AssistantWidget } from "../components/AssistantWidget";
import { CallButton } from "../components/VoiceProvider";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import { useZone } from "../context/ZoneContext";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { isNoldDomain } from "../lib/domain";
import { AdminZoneSwitch } from "../components/AdminZoneSwitch";
import { AppHeader } from "../components/AppHeader";
import { DashboardSideNav } from "../components/DashboardSideNav";
import RideMap from "../components/RideMap";
import EuropeRideForm from "../components/EuropeRideForm";
import { googleMapsDirUrl } from "../lib/geo";
import { WeatherWidget } from "../components/WeatherWidget";
import ChatSheet from "../components/ChatSheet";
import RatingSheet from "../components/RatingSheet";
import { ProfileSetup } from "../components/ProfileSetup";
import { COUNTRY_CENTERS, CM_CITIES, EU_CITIES, roadDistanceKm, reverseGeocode, getCurrentPosition, forwardGeocode, countryFromCoords } from "../lib/geo";
import { enablePush, pushSupported, notifPermission } from "../lib/push";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  MapPin, Navigation, Wallet, Star, History, User, Bike, Circle, Smartphone, Banknote, LocateFixed, MessageCircle, Phone, Home, Briefcase, Gift, Copy, ShieldCheck, Bell, Package, Car, Clock, Check,
} from "lucide-react";

const localMomoNum = (n) => (n || "").replace(/^\+?237/, "").replace(/\D/g, "");
const buildUssd = (op, num, amount) => {
  const ln = localMomoNum(num);
  if (!ln || op !== "mtn") return null; // MTN direct transfer only
  return `*126*9*${ln}*${Math.round(amount)}#`;
};

const PAY = [
  { id: "mtn", color: "#FFCC00", txt: "#000", icon: Smartphone },
  { id: "orange", color: "#FF6600", txt: "#fff", icon: Smartphone },
  { id: "cash", color: "#D94F2B", txt: "#fff", icon: Banknote },
  { id: "wallet", color: "#1A1A1A", txt: "#fff", icon: Wallet },
];

const statusColor = { requested: "#F26419", accepted: "#D94F2B", completed: "#75807A", cancelled: "#D32F2F" };
const SPEED = { moto: 26, car: 20, comfort: 20, bike: 16 };
const vtypeOf = (m) => (m === "car" ? "car" : m === "comfort" ? "comfort" : m === "velo" ? "bike" : "moto");
const isDeliv = (m) => m === "delivery" || m === "velo";

export default function PassengerDashboard() {
  const { t } = useTranslation();
  const { user, refresh, addRole, switchRole } = useAuth();
  const navigate = useNavigate();
  const [roleLoading, setRoleLoading] = useState("");
  const [verifyMode, setVerifyMode] = useState(false);
  const becomeDriver = async (intent) => {
    setRoleLoading(intent);
    try {
      localStorage.setItem("bsg_driver_intent", intent);
      await addRole("driver");
      navigate("/driver");
    } catch (e) {
      localStorage.removeItem("bsg_driver_intent");
      toast.error(apiError(e));
    } finally { setRoleLoading(""); }
  };
  const goDriver = async () => {
    setRoleLoading("switch");
    try { await switchRole("driver"); navigate("/driver"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setRoleLoading(""); }
  };
  const { current, currentCountry, setCountry } = useCountry();
  const { zoneView, isSuperAdmin } = useZone();
  const [tab, setTab] = useState("ride");
  const [mode, setMode] = useState("moto"); // moto | car | delivery | velo
  const [region, setRegion] = useState(() => {
    try { const s = localStorage.getItem("bsg_region"); return s ? JSON.parse(s) : null; } catch { return null; }
  }); // {zone, currency, currency_symbol, services, payment_methods, ...}
  const [needRegionChoice, setNeedRegionChoice] = useState(false);
  const [showEuCities, setShowEuCities] = useState(false);
  const isEurope = region?.zone === "europe";
  if (typeof localStorage !== "undefined" && region?.zone) { localStorage.setItem("bsg_zone", region.zone); try { localStorage.setItem("bsg_region", JSON.stringify(region)); } catch { /* ignore */ } }
  // Zone Europe = prix OBLIGATOIREMENT en € : on force un code pays européen pour l'estimation/réservation.
  const priceCC = isEurope ? ((region?.country_code || "BE").toUpperCase()) : current;
  const [item, setItem] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [altOffer, setAltOffer] = useState(null); // {other, eta, count} switch offer when selected vehicle unavailable

  const [pickup, setPickup] = useState("");
  const [dest, setDest] = useState("");
  const [distance, setDistance] = useState(5);
  const [payment, setPayment] = useState("mtn");
  const [estimate, setEstimate] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [waits, setWaits] = useState({});
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("now"); // now | later (Europe)
  const [scheduledFor, setScheduledFor] = useState("");
  const [pickupCoords, setPickupCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [chatRide, setChatRide] = useState(null);
  const [ratingRide, setRatingRide] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [gpsDenied, setGpsDenied] = useState(false);
  const [liveAgents, setLiveAgents] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [referral, setReferral] = useState(null);
  const [activation, setActivation] = useState(null);
  const [actNumber, setActNumber] = useState("");
  const [actCode, setActCode] = useState("");
  const [actDemo, setActDemo] = useState("");
  const [actBusy, setActBusy] = useState(false);
  const promptedRef = useRef(new Set());
  const acceptedNotifiedRef = useRef(new Set());
  const firstRidesLoadRef = useRef(true);
  const rideStageRef = useRef({});
  const [receiptRide, setReceiptRide] = useState(null);
  const promptedReceiptRef = useRef(new Set());
  const payLabelFr = (m) => (m === "cash" ? "Espèces" : m === "mobile" ? "Mobile Money" : (m || "").toUpperCase());
  const installDeferredRef = useRef(null);
  const preAlertRef = useRef(0);

  const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  // Prix affiché dans la devise de la zone détectée : € en Europe, FCFA en Afrique.
  const money = (amount) => (isEurope ? eur(amount) : fcfa(amount));
  // Prix affiché dans la devise propre à une course déjà créée.
  const rideMoney = (r) => (r?.currency === "EUR" ? eur(r.price) : fcfa(r?.price));

  // Détection de la zone (Europe / Afrique) via la géolocalisation, sinon choix manuel (option B).
  const detectRegion = async () => {
    let got = false;
    // Zone forcée : domaine noldgo.com → Europe ; sinon Vue Admin (super-admin) — prioritaire sur IP/GPS.
    const forcedZone = isNoldDomain() ? "europe" : (isSuperAdmin && zoneView ? zoneView : null);
    if (forcedZone) {
      const cc = forcedZone === "europe" ? "BE" : "CM";
      try {
        const { data } = await api.get(`/region/config?country_code=${cc}`);
        setRegion(data); setCountry(cc); setNeedRegionChoice(false);
        if (forcedZone === "europe") { setMode("car"); setPickupCoords({ lat: 50.8503, lng: 4.3517 }); setPickup("Bruxelles"); }
        else { setMode("moto"); }
      } catch { /* ignore */ }
      return;
    }
    // 1) Détection INSTANTANÉE par adresse IP (aucun bouton, aucune permission) — FR / BE / CM.
    try {
      const { data } = await api.get(`/region/detect`);
      if (data?.zone) {
        setRegion(data);
        if (data.country_code) setCountry(data.country_code);
        setNeedRegionChoice(false);
        if (data.zone === "europe") { setMode("car"); setPickupCoords({ lat: 50.8503, lng: 4.3517 }); setPickup("Bruxelles"); }
        got = true;
      }
    } catch { /* on tentera le GPS */ }
    // 2) Affinage FACULTATIF par GPS (ne bloque pas ; positionne le départ réel).
    try {
      const c = await getCurrentPosition();
      const cc = await countryFromCoords(c.lat, c.lng);
      {
        const { data } = cc
          ? await api.get(`/region/config?country_code=${cc}`)
          : await api.get(`/region/detect?lat=${c.lat}&lng=${c.lng}`);
        setRegion(data);
        if (cc) setCountry(cc); else if (data.country_code) setCountry(data.country_code);
        setNeedRegionChoice(false);
        setPickupCoords(c);
        setGpsDenied(false);
        reverseGeocode(c.lat, c.lng).then(setPickup).catch(() => {});
        if (data.zone === "europe" && !got) setMode("car");
        got = true;
      }
    } catch { setGpsDenied(true); /* GPS refusé → on garde la détection IP */ }
    if (!got) setNeedRegionChoice(true);
  };
  useEffect(() => { detectRegion(); /* eslint-disable-next-line */ }, [zoneView]);

  const chooseRegion = async (zone) => {
    const cc = zone === "europe" ? "BE" : "CM";
    try {
      const { data } = await api.get(`/region/config?country_code=${cc}`);
      setRegion(data);
      setCountry(cc);
      setNeedRegionChoice(false);
      setMode(zone === "europe" ? "car" : "moto");
      if (zone === "europe") {
        setPickupCoords({ lat: 50.8503, lng: 4.3517 });
        setPickup("Bruxelles");
        setDestCoords(null);
        setDest("");
      }
      toast.success(zone === "europe" ? "Zone Europe · prix en €" : "Zone Cameroun · prix en FCFA");
    } catch (e) { toast.error(apiError(e)); }
  };

  // Choix manuel d'une ville européenne → passe immédiatement en € + active le service Colis à vélo.
  const euSelectCity = async (name) => {
    const city = EU_CITIES.find((c) => c.name === name) || EU_CITIES[0];
    try {
      const { data } = await api.get(`/region/config?country_code=${city.cc || "BE"}`);
      setRegion(data);
      setCountry(city.cc || "BE");
      setNeedRegionChoice(false);
      setShowEuCities(false);
      setMode("velo"); // Colis à vélo activé
      setPickupCoords({ lat: city.lat, lng: city.lng });
      setPickup(city.name);
      setDestCoords(null);
      setDest("");
      toast.success(`Zone Europe · ${city.name} · prix en €`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadFavorites = () => api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  const loadReferral = () => api.get("/referral").then((r) => setReferral(r.data)).catch(() => {});
  const [wallet, setWallet] = useState(null);
  const [rechargeInfo, setRechargeInfo] = useState(null);
  const [rechargeModal, setRechargeModal] = useState(null); // "orange_money" | "mtn_momo"
  const [rcAmount, setRcAmount] = useState("");
  const [rcTxId, setRcTxId] = useState("");
  const [rcLoading, setRcLoading] = useState(false);
  const [rcProofPath, setRcProofPath] = useState("");
  const [rcProofPreview, setRcProofPreview] = useState("");
  const [rcProofUploading, setRcProofUploading] = useState(false);
  const loadWallet = () => api.get("/passenger/wallet").then((r) => setWallet(r.data)).catch(() => {});
  const loadRechargeInfo = () => api.get("/passenger/recharge-info").then((r) => setRechargeInfo(r.data)).catch(() => {});
  const openRecharge = (method) => { setRechargeModal(method); setRcAmount(""); setRcTxId(""); setRcProofPath(""); setRcProofPreview(""); };
  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choisissez une image (capture d'écran)."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image trop lourde (max 8 Mo)."); return; }
    setRcProofPreview(URL.createObjectURL(file));
    setRcProofUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/passenger/upload-proof", fd);
      setRcProofPath(data.proof_path);
      toast.success("Reçu joint ✓");
    } catch (err) { setRcProofPreview(""); toast.error(apiError(err)); }
    finally { setRcProofUploading(false); }
  };
  const submitRecharge = async () => {
    const amount = Number(rcAmount);
    if (!amount || amount <= 0) { toast.error("Montant invalide."); return; }
    if (!rcTxId.trim()) { toast.error("L'ID de transaction (reçu par SMS) est obligatoire."); return; }
    if (!rcProofPath) { toast.error("Joignez la capture d'écran du reçu Mobile Money (obligatoire)."); return; }
    setRcLoading(true);
    try {
      await api.post("/passenger/wallet/recharge", { amount, method: rechargeModal, transaction_id: rcTxId.trim(), proof_path: rcProofPath || undefined });
      toast.success("Recharge enregistrée. En attente de validation par l'administrateur ✅");
      setRechargeModal(null);
      loadWallet();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRcLoading(false); }
  };
  const campayPay = async () => {
    const amount = Number(rcAmount);
    if (!amount || amount <= 0) { toast.error("Entrez le montant à recharger."); return; }
    setRcLoading(true);
    try {
      const { data } = await api.post("/wallet/campay/recharge", { amount, method: rechargeModal });
      if (data.pay_url) window.open(data.pay_url, "_blank");
      toast.info("Validez le paiement sur votre téléphone (Orange Money / MTN MoMo)…", { duration: 8000 });
      const uuid = data.uuid; let done = false;
      for (let i = 0; i < 24 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const s = await api.get(`/wallet/campay/status/${uuid}`);
          if (s.data.status === "completed") { done = true; toast.success("Paiement reçu ✅ Portefeuille crédité."); setRechargeModal(null); loadWallet(); }
          else if (["failed", "cancelled"].includes(s.data.status)) { done = true; toast.error("Paiement échoué ou annulé."); }
        } catch {}
      }
      if (!done) toast.message("En attente de confirmation… votre solde se mettra à jour dès réception.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRcLoading(false); }
  };
  useEffect(() => { loadFavorites(); loadReferral(); loadWallet(); loadRechargeInfo(); /* geo + region via detectRegion */ /* eslint-disable-next-line */ }, []);

  const openRechargeReceipt = (t2) => {
    const methodLabel = t2.method === "mtn_momo" ? "MTN MoMo" : "Orange Money";
    const dt = new Date(t2.approved_at || t2.created_at || Date.now());
    const amount = (t2.amount || 0).toLocaleString("fr-FR");
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Reçu de recharge Mboa Go</title>
<style>
  body{font-family:'Segoe UI',system-ui,sans-serif;margin:0;background:#F5F1E8;padding:24px;color:#1A1A1A}
  .card{max-width:420px;margin:0 auto;background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 8px 30px rgba(0,0,0,.12)}
  .head{background:linear-gradient(135deg,#FFC531,#F26419);padding:24px;text-align:center;color:#fff}
  .head h1{margin:0;font-size:24px;font-weight:800}
  .head p{margin:4px 0 0;font-size:13px;opacity:.95}
  .body{padding:24px}
  .row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #F0EBE1;font-size:14px}
  .row .lbl{color:#75807A}.row .val{font-weight:700}
  .total{margin-top:16px;background:#FFF7E8;border-radius:12px;padding:16px;text-align:center}
  .total .amt{font-size:32px;font-weight:800;color:#16a34a}
  .badge{display:inline-block;margin-top:8px;background:#16a34a;color:#fff;font-size:12px;font-weight:700;padding:4px 12px;border-radius:20px}
  .muted{text-align:center;color:#75807A;font-size:12px;margin-top:16px}
  button{margin-top:16px;width:100%;height:44px;border:0;border-radius:10px;background:#F26419;color:#fff;font-weight:700;font-size:15px;cursor:pointer}
  @media print{button{display:none}body{background:#fff;padding:0}.card{box-shadow:none}}
</style></head><body>
<div class="card">
  <div class="head"><h1>Mboa Go</h1><p>Reçu de recharge portefeuille</p></div>
  <div class="body">
    <div class="row"><span class="lbl">Reçu N°</span><span class="val">${t2.receipt_no ? "#" + t2.receipt_no : "—"}</span></div>
    <div class="row"><span class="lbl">Date</span><span class="val">${dt.toLocaleString("fr-FR")}</span></div>
    <div class="row"><span class="lbl">Moyen de paiement</span><span class="val">${methodLabel}</span></div>
    ${t2.transaction_id ? `<div class="row"><span class="lbl">ID transaction</span><span class="val">${t2.transaction_id}</span></div>` : ""}
    <div class="total"><div class="lbl">Montant crédité</div><div class="amt">+${amount} FCFA</div><div class="badge">✓ Recharge validée</div></div>
    <p class="muted">Merci d'utiliser Mboa Go 🏍️<br/>www.bentskingo.com</p>
    <button onclick="window.print()">🖨️ Imprimer / Enregistrer en PDF</button>
  </div>
</div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { const a = document.createElement("a"); a.href = url; a.download = `recu-recharge-bentskin-${(t2.receipt_no || t2.id || "").toString().slice(0, 8)}.html`; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  useEffect(() => {
    const intent = localStorage.getItem("bsg_intent");
    if (!intent) return;
    localStorage.removeItem("bsg_intent");
    setTab("ride");
    if (intent === "delivery") setMode("delivery");
    else if (intent === "car") setMode("car");
    else if (intent === "moto") setMode("moto");
  }, []);

  const pickFavorite = (f) => {
    setDest(f.address);
    if (f.lat != null) setDestCoords({ lat: f.lat, lng: f.lng });
  };

  const saveFavorite = async (label) => {
    if (!dest.trim()) return toast.error(t("pax.destPh"));
    try {
      const { data } = await api.post("/favorites", { label, address: dest, lat: destCoords?.lat, lng: destCoords?.lng });
      setFavorites((f) => [...f, data]);
      toast.success(t("pax.favSaved"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeFavorite = async (id) => {
    try { await api.delete(`/favorites/${id}`); setFavorites((f) => f.filter((x) => x.id !== id)); } catch {}
  };

  const waLink = (phone, msg) => {
    const digits = String(phone || "").replace(/[^0-9]/g, "");
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg || "")}`;
  };

  const downloadReceipt = (r) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const service = r.type === "delivery" ? "Livraison 📦" : r.vehicle_type === "car" ? "Taxi 🚕" : "Ben-skin 🛵";
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Reçu Mboa Go</title>
<style>body{font-family:system-ui,sans-serif;color:#1A1A1A;max-width:520px;margin:24px auto;padding:24px}
.h{display:flex;align-items:center;gap:10px;border-bottom:3px solid #D94F2B;padding-bottom:12px}
.h b{font-size:22px}.h .g{color:#F26419}
.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;font-size:14px}
.tot{font-size:20px;font-weight:800;margin-top:8px}.muted{color:#75807A}</style></head><body>
<div class="h"><b>Mboa<span class="g"> Go</span></b></div>
${(r.pickup_lat&&r.dest_lat)?`<img src="https://staticmap.openstreetmap.de/staticmap.php?size=472x170&maptype=mapnik&markers=${r.pickup_lat},${r.pickup_lng},lightgreen|${r.dest_lat},${r.dest_lng},red" alt="Trajet" style="width:100%;border-radius:10px;margin:12px 0;border:1px solid #eee" />`:''}
<h3>Reçu — ${esc(service)}</h3>
<div class="row"><span class="muted">Service</span><span>${esc(service)}</span></div>
<div class="row"><span class="muted">Réf.</span><span>${esc(r.id.slice(0,8).toUpperCase())}</span></div>
<div class="row"><span class="muted">Date</span><span>${esc(new Date(r.completed_at||r.created_at).toLocaleString('fr-FR'))}</span></div>
<div class="row"><span class="muted">Départ</span><span>${esc(r.pickup||'-')}</span></div>
<div class="row"><span class="muted">Destination</span><span>${esc(r.destination||'-')}</span></div>
<div class="row"><span class="muted">Chauffeur</span><span>${esc(r.driver_name||'-')} · ${esc(r.driver_plate||'')}</span></div>
<div class="row"><span class="muted">Distance</span><span>${esc(r.distance_km)} km</span></div>
<div class="row"><span class="muted">Paiement</span><span>${r.currency==='EUR'?'Carte bancaire (Stripe)':esc(r.payment_method_final==='cash'||r.payment_method==='cash'?'Espèces':(r.payment_method_final==='mobile'?'Mobile Money':(r.payment_method_final||r.payment_method||'').toUpperCase()))}</span></div>
${r.discount?`<div class="row"><span class="muted">Réduction crédit</span><span>-${r.currency==='EUR'?`${(r.discount).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})} €`:`${(r.discount).toLocaleString('fr-FR')} FCFA`}</span></div>`:''}
<div class="row tot"><span>Total payé</span><span>${r.currency==='EUR'?`${(r.price).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})} €`:`${(r.price).toLocaleString('fr-FR')} FCFA`}</span></div>
<p class="muted">Merci d'avoir voyagé avec Mboa Go 🏍️</p>
<button onclick="window.print()" style="margin-top:16px;width:100%;height:44px;border:0;border-radius:10px;background:#F26419;color:#fff;font-weight:700;font-size:15px;cursor:pointer">🖨️ Imprimer / Enregistrer en PDF</button>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { const a = document.createElement("a"); a.href = url; a.download = `recu-bentskin-${r.id.slice(0,8)}.html`; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const copyReferral = () => {
    const link = `${window.location.origin}/auth?role=passenger&ref=${referral?.code}`;
    navigator.clipboard?.writeText(link);
    toast.success(t("referral.copied"));
  };

  const shareReferral = () => {
    const link = `${window.location.origin}/auth?role=passenger&ref=${referral?.code}`;
    const msg = `🛵 Rejoins Mboa Go (bendskin à Yaoundé) ! C'est gratuit pendant le lancement. Inscris-toi avec mon code ${referral?.code} 👉 ${link}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    if (navigator.share) { navigator.share({ title: "Mboa Go", text: msg, url: link }).catch(() => window.open(wa, "_blank")); }
    else window.open(wa, "_blank");
  };

  const shareTrip = async (r) => {
    try {
      const np = window.prompt("Numéro du proche à prévenir automatiquement à la fin du trajet (facultatif, ex : +237 6XX XXX XXX) :", "");
      const { data } = await api.post(`/rides/${r.id}/share`, np && np.trim() ? { notify_phone: np.trim() } : {});
      const link = data.url;
      const msg = `🛡️ Suivez mon trajet Mboa Go en direct 👉 ${link}\nChauffeur : ${r.driver_name || "—"}${r.driver_plate ? ` (${r.driver_plate})` : ""}. Vérifiez que j'arrive bien 🙏`;
      const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      if (navigator.share) { navigator.share({ title: "Mon trajet Mboa Go", text: msg, url: link }).catch(() => window.open(wa, "_blank")); }
      else window.open(wa, "_blank");
      if (np && np.trim()) toast.success("Le proche sera prévenu à la fin du trajet (simulation SMS).");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [notifOn, setNotifOn] = useState(notifPermission() === "granted");
  const enableNotif = async () => {
    try { await enablePush(); setNotifOn(true); toast.success(t("notif.enabled")); }
    catch { toast.error(t("notif.denied")); }
  };
  useEffect(() => { if (pushSupported() && notifPermission() === "granted") enablePush().catch(() => {}); }, []);

  const EU_CENTER = { lat: 50.8503, lng: 4.3517, name: "Bruxelles" };
  const mapCenter = pickupCoords || (isEurope ? EU_CENTER : (COUNTRY_CENTERS[current] || COUNTRY_CENTERS.CM));

  const locateMe = async () => {
    setLocating(true);
    try {
      const c = await getCurrentPosition();
      setPickupCoords(c);
      setGpsDenied(false);
      const addr = await reverseGeocode(c.lat, c.lng);
      setPickup(addr);
      toast.success(t("pax.locateOk"));
    } catch (err) {
      const denied = err && err.code === 1;
      setGpsDenied(true);
      // fallback to country center so the demo still works without a GPS fix
      const c = { lat: mapCenter.lat, lng: mapCenter.lng };
      setPickupCoords(c);
      setPickup(mapCenter.name);
      toast.info(denied
        ? "Localisation refusée. Autorisez la position dans les réglages de votre navigateur, ou saisissez votre adresse de départ."
        : "Position introuvable pour le moment (signal GPS faible). Réessayez à l'air libre ou saisissez votre adresse de départ.");
    } finally {
      setLocating(false);
    }
  };

  const onMapClick = async (c) => {
    if (!pickupCoords) {
      setPickupCoords(c);
      setPickup(await reverseGeocode(c.lat, c.lng));
    } else {
      setDestCoords(c);
      setDest(await reverseGeocode(c.lat, c.lng));
    }
  };

  const selectCity = (city) => {
    setPickupCoords({ lat: city.lat, lng: city.lng });
    setPickup(city.name);
    setDestCoords(null);
    setDest("");
    toast.success(`Départ : ${city.name}`);
  };

  // Distance ROUTIÈRE réelle (comme Uber/Yango) via le backend (OSRM), repli sur estimation.
  useEffect(() => {
    if (!pickupCoords || !destCoords) return;
    let cancelled = false;
    api.post("/rides/route", {
      origin_lat: pickupCoords.lat, origin_lng: pickupCoords.lng,
      dest_lat: destCoords.lat, dest_lng: destCoords.lng,
    })
      .then((r) => { if (!cancelled && r.data?.distance_km) setDistance(r.data.distance_km); })
      .catch(() => { if (!cancelled) setDistance(roadDistanceKm(pickupCoords, destCoords)); });
    return () => { cancelled = true; };
  }, [pickupCoords, destCoords]);

  // Alerte de proximité (pré-dispatch) : prévient les chauffeurs proches dès qu'une destination est saisie (course non validée)
  useEffect(() => {
    if (!pickupCoords || !destCoords) return;
    const now = Date.now();
    if (now - preAlertRef.current < 45000) return;
    preAlertRef.current = now;
    api.post("/rides/pre-alert", { pickup_lat: pickupCoords.lat, pickup_lng: pickupCoords.lng, vehicle_type: vtypeOf(mode) }).catch(() => {});
  }, [pickupCoords, destCoords]); // eslint-disable-line

  const playRingtone = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      [880, 1175, 880].forEach((f, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
        const t0 = ctx.currentTime + i * 0.18;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
        o.start(t0); o.stop(t0 + 0.18);
      });
    } catch { /* ignore */ }
  };
  const loadRides = () => api.get("/rides/mine").then((r) => {
    const list = r.data || [];
    const first = firstRidesLoadRef.current;
    list.forEach((x) => {
      if (x.status === "accepted" && !acceptedNotifiedRef.current.has(x.id)) {
        acceptedNotifiedRef.current.add(x.id);
        if (!first) {
          playRingtone();
          toast.success("🎉 Votre chauffeur a accepté votre course !");
          try { if ("Notification" in window && Notification.permission === "granted") new Notification("Mboa Go", { body: "🎉 Votre chauffeur a accepté votre course !" }); } catch { /* ignore */ }
        }
      }
      const prev = rideStageRef.current[x.id] || {};
      const notifyStage = (msg) => {
        if (first) return;
        playRingtone();
        toast.success(msg);
        try { if ("Notification" in window && Notification.permission === "granted") new Notification("Mboa Go", { body: msg }); } catch { /* ignore */ }
      };
      if (x.arrived_at && !prev.arrived) notifyStage("📍 Votre chauffeur est arrivé au point de rendez-vous !");
      if (x.trip_started && !prev.started) notifyStage("🛵 Course démarrée — bon voyage !");
      if (x.status === "completed" && !prev.completed) notifyStage("✅ Course terminée. Merci d'avoir voyagé avec Mboa Go !");
      rideStageRef.current[x.id] = { arrived: !!x.arrived_at, started: !!x.trip_started, completed: x.status === "completed" };
    });
    firstRidesLoadRef.current = false;
    setRides(list);
    if (list.some((x) => x.status === "accepted" && x.payment_pending)) setTab((cur) => (cur === "ride" ? "history" : cur));
  }).catch(() => {});
  useEffect(() => { loadRides(); const iv = setInterval(loadRides, 20000); return () => clearInterval(iv); }, []);
  // Poll faster (Uber-style) while a driver is en route to track the moto in near real-time
  useEffect(() => {
    if (!rides.some((r) => r.status === "accepted")) return;
    const iv = setInterval(loadRides, 15000);
    return () => clearInterval(iv);
  }, [rides]); // eslint-disable-line

  const cancelRide = async (id) => {
    if (!window.confirm(t("pax.cancelConfirm"))) return;
    try { await api.post(`/rides/${id}/cancel`); toast.success(t("pax.cancelled")); loadRides(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [payMethod, setPayMethod] = useState(null);
  const [driverQr, setDriverQr] = useState(null);
  const showMobileQr = async (rideId) => {
    try { const { data } = await api.get(`/rides/${rideId}/payment-qr`); setDriverQr(data); setPayMethod("mobile"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const iPaid = async (rideId, method) => {
    try { await api.post(`/rides/${rideId}/client-paid`, { method }); toast.success(t("pay.reported")); loadRides(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Auto-open the rating screen once a trip is completed and not yet rated (after receipt)
  useEffect(() => {
    if (ratingRide || receiptRide) return;
    const pending = rides.find((r) => r.status === "completed" && !r.rating && !promptedRef.current.has(r.id));
    if (pending) { promptedRef.current.add(pending.id); setRatingRide(pending); setTab("history"); }
  }, [rides, ratingRide, receiptRide]);

  // Auto-show the digital receipt right after a ride completes (recent only, once per ride)
  useEffect(() => {
    if (receiptRide) return;
    const done = rides.find((r) => r.status === "completed" && !promptedReceiptRef.current.has(r.id)
      && r.completed_at && (Date.now() - new Date(r.completed_at).getTime() < 5 * 60 * 1000));
    if (done) { promptedReceiptRef.current.add(done.id); setReceiptRide(done); setTab("history"); }
  }, [rides, receiptRide]);

  // Capture the install prompt so we can offer it after a completed ride
  useEffect(() => {
    const h = (e) => { e.preventDefault(); installDeferredRef.current = e; };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const promptRideInstall = async () => {
    const d = installDeferredRef.current;
    if (d) { d.prompt(); await d.userChoice; installDeferredRef.current = null; return; }
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    toast.info(ios ? t("install.iosHint") : t("install.androidStep2"), { duration: 8000 });
  };

  // Offer to install the app once, after the first completed ride
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone || localStorage.getItem("bsg_ride_install_prompted")) return;
    if (!rides.some((r) => r.status === "completed")) return;
    localStorage.setItem("bsg_ride_install_prompted", "1");
    const timer = setTimeout(() => {
      toast(t("install.rideTitle"), {
        description: t("install.rideText"),
        duration: 12000,
        action: { label: t("install.cta"), onClick: promptRideInstall },
      });
    }, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [rides]);

  // Nearby available motorbikes around the pickup / map center
  useEffect(() => {
    const p = pickupCoords || mapCenter;
    const vt = vtypeOf(mode);
    api.get(`/drivers/nearby?lat=${p.lat}&lng=${p.lng}&country_code=${current}&vehicle_type=${vt}`).then((r) => setNearby(r.data)).catch(() => {});
  }, [pickupCoords, current, mode]); // eslint-disable-line

  // Live online field agents (blue markers) — refresh every 5s
  useEffect(() => {
    const loadAgents = () => api.get("/live/agents").then((r) => setLiveAgents(r.data.agents || [])).catch(() => {});
    loadAgents();
    const iv = setInterval(loadAgents, 30000);
    return () => clearInterval(iv);
  }, []);

  // If the selected vehicle has none nearby, check the other vehicle type to offer a switch
  useEffect(() => {
    if (mode === "delivery" || nearby.length > 0) { setAltOffer(null); return; }
    const other = mode === "car" ? "moto" : "car";
    const p = pickupCoords || mapCenter;
    api.get(`/drivers/nearby?lat=${p.lat}&lng=${p.lng}&country_code=${current}&vehicle_type=${other}`)
      .then((r) => {
        const list = r.data || [];
        if (list.length === 0) { setAltOffer({ other: null }); return; }
        const km = list[0]?.distance_km;
        const eta = km != null ? Math.max(1, Math.round((km / SPEED[other]) * 60)) : 3;
        setAltOffer({ other, eta, count: list.length });
      })
      .catch(() => setAltOffer(null));
  }, [mode, nearby, pickupCoords, current]); // eslint-disable-line

  const doEstimate = async () => {
    try {
      const r = await api.post("/rides/estimate", { country_code: priceCC, distance_km: Number(distance), vehicle_type: vtypeOf(mode), is_delivery: isDeliv(mode) });
      setEstimate(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { doEstimate(); /* eslint-disable-next-line */ }, [distance, current, mode]);

  // Quotes for every service so passenger can compare price before choosing (Uber/Yango style)
  useEffect(() => {
    let cancelled = false;
    const ms = ["moto", "car", "comfort", "delivery", "velo"];
    Promise.all(ms.map((m) =>
      api.post("/rides/estimate", { country_code: priceCC, distance_km: Number(distance), vehicle_type: vtypeOf(m), is_delivery: isDeliv(m) })
        .then((r) => [m, r.data.price]).catch(() => [m, null])
    )).then((entries) => { if (!cancelled) setQuotes(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [distance, current]);

  // Real per-service wait time from nearest available driver of each vehicle type
  useEffect(() => {
    const p = pickupCoords || mapCenter;
    if (!p) return;
    let cancelled = false;
    const vts = ["moto", "car", "bike"];
    Promise.all(vts.map((vt) =>
      api.get(`/drivers/nearby?lat=${p.lat}&lng=${p.lng}&country_code=${current}&vehicle_type=${vt}`)
        .then((r) => {
          const list = r.data || [];
          const km = list[0]?.distance_km;
          const eta = km != null ? Math.max(1, Math.round((km / SPEED[vt]) * 60)) : null;
          return [vt, { eta, count: list.length }];
        }).catch(() => [vt, { eta: null, count: 0 }])
    )).then((entries) => { if (!cancelled) setWaits(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [pickupCoords, current]); // eslint-disable-line

  const loadActivation = () => api.get("/passenger/subscription").then((r) => setActivation(r.data)).catch(() => {});
  const doSubscribe = async () => {
    setActBusy(true);
    try {
      const { data } = await api.post("/passenger/subscription/subscribe");
      toast.success("Compte activé ✅ Vous pouvez commander vos courses (aucun abonnement).");
      setActivation(data); loadWallet();
    } catch (e) { toast.error(apiError(e)); }
    finally { setActBusy(false); }
  };
  useEffect(() => { loadActivation(); /* eslint-disable-next-line */ }, []);

  const book = async () => {
    if (!isEurope && activation?.required && !activation?.active)
      return toast.error("Activez votre compte (600 FCFA, une seule fois) pour commander — voir la carte en haut.");
    if (!pickup.trim() || !dest.trim()) return toast.error(t("pax.pickup") + " / " + t("pax.dest"));
    if (isDeliv(mode) && (!item.trim() || !recipientName.trim() || !recipientPhone.trim()))
      return toast.error(t("delivery.missing"));
    if (payment === "wallet" && wallet && estimate && Number(wallet.balance || 0) < Number(estimate.price || 0))
      return toast.error("Solde insuffisant. Rechargez votre portefeuille pour payer cette course.");
    setLoading(true);
    // Ouvre l'onglet de paiement DANS le geste de clic : évite le blocage popup et l'iframe (Stripe Checkout refuse d'être affiché en iframe).
    const payWin = isEurope ? window.open("", "_blank") : null;
    try {
      let pc = pickupCoords, dc = destCoords;
      if (!pc) pc = await forwardGeocode(pickup, current);
      if (!dc) dc = await forwardGeocode(dest, current);
      if (!pc) { if (payWin) payWin.close(); setLoading(false); return toast.error("Confirmez votre point de départ : choisissez une adresse dans la liste ou activez votre position GPS."); }
      if (isEurope) {
        // ZONE EUROPE : paiement Stripe (€) AVANT la mise en course, puis redirection Stripe Checkout.
        const vt = mode === "car" ? "car" : mode === "comfort" ? "comfort" : "delivery_bike";
        const { data } = await api.post("/rides/eu-checkout", {
          country_code: priceCC, pickup, destination: dest, distance_km: Number(distance),
          pickup_lat: pc?.lat, pickup_lng: pc?.lng, dest_lat: dc?.lat, dest_lng: dc?.lng,
          type: mode === "velo" ? "delivery" : "passenger",
          vehicle_type: vt,
          item_description: item || null, recipient_name: recipientName || null, recipient_phone: recipientPhone || null,
          origin_url: window.location.origin,
        });
        if (data?.checkout_url) {
          if (payWin) payWin.location.href = data.checkout_url;
          else window.location.href = data.checkout_url;
        } else {
          if (payWin) payWin.close();
          toast.error("Paiement Stripe indisponible. Réessayez dans un instant.");
        }
        setLoading(false);
        return;
      }
      const { data } = await api.post("/rides", {
        country_code: current, pickup, destination: dest, distance_km: Number(distance), payment_method: payment,
        pickup_lat: pc?.lat, pickup_lng: pc?.lng,
        dest_lat: dc?.lat, dest_lng: dc?.lng,
        type: isDeliv(mode) ? "delivery" : "passenger",
        vehicle_type: vtypeOf(mode),
        item_description: item || null, recipient_name: recipientName || null, recipient_phone: recipientPhone || null,
      });
      if (data.driver_id) toast.success(`${t("pax.driverAssigned")} · ${data.driver_name}`);
      else toast.success(isDeliv(mode) ? t("delivery.bookedOk") : (mode === "car" || mode === "comfort") ? t("delivery.okCar") : t("delivery.okMoto"));
      setPickup(""); setDest(""); setPickupCoords(null); setDestCoords(null);
      setItem(""); setRecipientName(""); setRecipientPhone("");
      loadRides();
      setTab("history");
    } catch (e) { if (payWin) payWin.close(); toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const tabs = [
    { id: "ride", label: t("pax.tabRide"), icon: Navigation },
    { id: "history", label: t("pax.tabHistory"), icon: History },
    { id: "profile", label: t("pax.tabProfile"), icon: User },
  ];

  const etaSpeed = SPEED[mode === "car" ? "car" : mode === "velo" ? "bike" : "moto"];
  const tripEta = distance ? Math.max(2, Math.round((distance / etaSpeed) * 60)) : 0;
  const nearestKm = nearby[0]?.distance_km;
  const noDriver = !isEurope && nearby.length === 0;
  const pickupEta = nearestKm != null ? Math.max(1, Math.round((nearestKm / etaSpeed) * 60)) : null;

  if (verifyMode) {
    return <ProfileSetup onDone={() => { setVerifyMode(false); refresh(); }} onClose={() => setVerifyMode(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#FFF9F2] pb-24">
      <AssistantWidget />
      <DashboardSideNav tabs={tabs} active={tab} onSelect={setTab} />
      <div className="lg:pl-72">
      <div className="lg:hidden"><AppHeader variant="app" /></div>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <WeatherWidget lat={mapCenter?.lat} lng={mapCenter?.lng} city={mapCenter?.name} />
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-head font-black text-2xl tracking-tight">
          {t("pax.greeting")}, {user?.name || t("hero.passenger")} 👋
        </motion.h1>

        <AnimatePresence mode="wait">
          {tab === "ride" && (
            <motion.div key="ride" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6 space-y-4">
              {!isEurope && activation?.required && !activation?.active && (
                <div data-testid="pax-subscription-card" className="rounded-3xl overflow-hidden bg-white border-2 border-[#16a34a]">
                  <div className="bg-gradient-to-br from-[#16a34a] to-[#0f8a3c] text-white p-5">
                    <div className="font-head font-black text-xl tracking-tight">Activez votre compte pour commander</div>
                    <p className="mt-1 text-sm text-white/90 font-semibold">Activation <b>unique</b> : <b>{fcfa(activation?.fee || 600)}</b> — une seule fois, prélevée sur votre portefeuille. Aucun abonnement.</p>
                  </div>
                  <div className="p-5">
                    <div className="rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-4 flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#75807A]">Solde portefeuille</span>
                      <span data-testid="pax-sub-wallet" className="font-bold text-[#1A1A1A]">{fcfa(activation?.wallet || 0)}</span>
                    </div>
                    {Number(activation?.wallet || 0) < (activation?.fee || 600) ? (
                      <>
                        <p data-testid="pax-sub-insufficient" className="mt-3 text-xs text-[#B8860B]">Solde insuffisant. Rechargez votre portefeuille d'au moins {fcfa(activation?.fee || 600)} pour activer votre compte.</p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <button data-testid="pax-sub-recharge-orange" onClick={() => openRecharge("orange_money")} className="h-12 rounded-xl bg-[#FF6600] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2 text-sm"><Smartphone className="w-4 h-4" /> Orange Money</button>
                          <button data-testid="pax-sub-recharge-mtn" onClick={() => openRecharge("mtn_momo")} className="h-12 rounded-xl bg-[#FFCC00] hover:opacity-90 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2 text-sm"><Smartphone className="w-4 h-4" /> MTN MoMo</button>
                        </div>
                      </>
                    ) : (
                      <Button data-testid="pax-subscribe-btn" disabled={actBusy} onClick={doSubscribe} className="mt-3 w-full h-12 rounded-xl bg-[#16a34a] hover:bg-[#0f8a3c] text-white font-bold disabled:opacity-50">{actBusy ? "…" : `Payer ${fcfa(activation?.fee || 600)} & activer mon compte`}</Button>
                    )}
                  </div>
                </div>
              )}
              {needRegionChoice && (
                <div data-testid="region-choice" className="rounded-3xl border-2 border-[#F26419] bg-[#FFF3E9] p-5">
                  <div className="font-head font-bold text-[#1A1A1A]">Où êtes-vous ?</div>
                  <p className="text-xs text-[#75807A] mt-0.5">Choisissez votre zone pour afficher les bons services, prix et paiements.</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <button data-testid="choose-region-africa" onClick={() => chooseRegion("africa")} className="h-14 rounded-2xl bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] font-bold text-sm text-[#1A1A1A] flex items-center justify-center gap-2 transition-colors">🇨🇲 Cameroun · FCFA</button>
                    <button data-testid="choose-region-europe" onClick={() => setShowEuCities(true)} className={`h-14 rounded-2xl bg-white border-2 font-bold text-sm text-[#1A1A1A] flex items-center justify-center gap-2 transition-colors ${showEuCities ? "border-[#2563EB]" : "border-[#E2DFD8] hover:border-[#2563EB]"}`}>🇪🇺 Europe · €</button>
                  </div>
                  {showEuCities && (
                    <div className="mt-3" data-testid="eu-city-select-wrap">
                      <div className="text-xs font-semibold text-[#75807A] mb-1.5">Choisissez votre ville</div>
                      <Select onValueChange={euSelectCity}>
                        <SelectTrigger data-testid="eu-city-select" className="h-12 rounded-xl border-2 bg-white">
                          <SelectValue placeholder="Sélectionner une ville…" />
                        </SelectTrigger>
                        <SelectContent>
                          {["Bruxelles", "Anvers", "Liège", "Gand", "Paris"].map((c) => (
                            <SelectItem key={c} value={c} data-testid={`eu-city-${c}`}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-[#75807A] mt-1.5">Dès la sélection, les prix passent en € et le service Colis à vélo est activé.</p>
                    </div>
                  )}
                </div>
              )}
              {region && (
                <button type="button" data-testid="zone-pill" onClick={() => { setShowEuCities(false); setNeedRegionChoice(true); }} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${isEurope ? "bg-[#2563EB]/10 text-[#2563EB] hover:bg-[#2563EB]/20" : "bg-[#F26419]/15 text-[#D94F2B] hover:bg-[#F26419]/25"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isEurope ? "bg-[#2563EB]" : "bg-[#F26419]"}`} />
                  {isEurope ? "Zone Europe · paiement par carte (€)" : "Zone Cameroun · Cash / Mobile Money (FCFA)"}
                  <span className="opacity-60">· Changer</span>
                </button>
              )}
              {isEurope && region && (
                <EuropeRideForm
                  region={region}
                  pickup={pickup} setPickup={setPickup} dest={dest} setDest={setDest}
                  pickupCoords={pickupCoords} setPickupCoords={setPickupCoords}
                  destCoords={destCoords} setDestCoords={setDestCoords}
                  mode={mode} setMode={setMode} quotes={quotes} money={money}
                  loading={loading} onBook={book}
                  mapCenter={mapCenter} onMapClick={onMapClick} locateMe={locateMe} locating={locating}
                  item={item} setItem={setItem} recipientName={recipientName} setRecipientName={setRecipientName}
                  recipientPhone={recipientPhone} setRecipientPhone={setRecipientPhone}
                />
              )}
              {!isEurope && (<>
              <div className="space-y-2" data-testid="mode-toggle">
                <h2 className="font-head font-bold text-lg px-1">{t("pax.chooseRide")}</h2>
                {[
                  { id: "moto", label: t("delivery.modeMoto"), sub: t("pax.subMoto"), Icon: Bike, color: "#F26419" },
                  { id: "car", label: t("delivery.modeCar"), sub: t("pax.subCar"), Icon: Car, color: "#2563EB" },
                  { id: "comfort", label: t("delivery.modeComfort"), sub: t("pax.subComfort"), Icon: Car, color: "#7C3AED" },
                  { id: "delivery", label: t("delivery.modeParcel"), sub: t("pax.subParcel"), Icon: Package, color: "#D94F2B" },
                  { id: "velo", label: t("delivery.modeVelo"), sub: t("pax.subVelo"), Icon: Bike, color: "#16a34a" },
                ].filter((o) => (isEurope ? (o.id === "car" || o.id === "velo") : true)).map((o) => {
                  const active = mode === o.id;
                  const price = quotes[o.id];
                  const w = waits[vtypeOf(o.id)];
                  const count = w?.count || 0;
                  const waitEta = w?.eta;
                  const unavailable = !isEurope && w !== undefined && count === 0;
                  return (
                    <button key={o.id} data-testid={`mode-${o.id}`} disabled={unavailable} onClick={() => !unavailable && setMode(o.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ${unavailable ? "border-[#F0E6DA] bg-[#F7F3EC] opacity-60 cursor-not-allowed" : active ? "border-[#D94F2B] bg-[#FFF3E9] shadow-[0_4px_16px_rgba(255,107,0,0.12)]" : "border-[#F0E6DA] bg-white hover:border-[#E2DFD8]"}`}>
                      <span className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center" style={{ background: `${o.color}1A` }}>
                        <o.Icon className="w-5 h-5" style={{ color: o.color }} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#1A1A1A]">{o.label}</div>
                        <div className="text-xs flex items-center gap-1.5">
                          <span className="truncate text-[#75807A]">{o.sub}</span>
                          {isEurope ? (
                            <span data-testid={`wait-${o.id}`} className="inline-flex items-center gap-1 shrink-0 text-[#16a34a] font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a]" /> Disponible
                            </span>
                          ) : count > 0 ? (
                            <span data-testid={`wait-${o.id}`} className="inline-flex items-center gap-1 shrink-0 text-[#16a34a] font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a] animate-pulse" /> ≈ {waitEta} {t("common.min")} {t("pax.wait")}
                            </span>
                          ) : (
                            <span data-testid={`wait-${o.id}`} className="shrink-0 text-[#D32F2F] font-medium">· {t("pax.noDriverShort")}</span>
                          )}
                        </div>
                      </div>
                      <div className="font-head font-bold text-sm text-[#1A1A1A] shrink-0" data-testid={`quote-${o.id}`}>{price != null ? money(price) : "—"}</div>
                    </button>
                  );
                })}
              </div>
              {!isEurope && !isDeliv(mode) && nearby.length === 0 && altOffer && (
                <div data-testid="vehicle-switch-suggestion" className="rounded-2xl bg-[#F26419]/15 border border-[#F26419] p-4">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-xl bg-[#F26419] flex items-center justify-center">
                      {altOffer.other === "car" ? <Car className="w-5 h-5 text-[#1A1A1A]" /> : <Bike className="w-5 h-5 text-[#1A1A1A]" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#1A1A1A]">{altOffer.other ? t("delivery.noVehTitle", { veh: (mode === "car" ? t("delivery.modeCar") : t("delivery.modeMoto")).toLowerCase() }) : t("delivery.noVehTitleNone")}</div>
                      {altOffer.other && (
                        <div className="text-xs text-[#75807A]">{t("delivery.switchText", { veh: (altOffer.other === "car" ? t("delivery.modeCar") : t("delivery.modeMoto")).toLowerCase(), eta: altOffer.eta })}</div>
                      )}
                    </div>
                    {altOffer.other && (
                      <button data-testid="switch-vehicle" onClick={() => setMode(altOffer.other)} className="shrink-0 h-10 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white text-sm font-bold transition-colors">{t("delivery.switchTo", { veh: (altOffer.other === "car" ? t("delivery.modeCar") : t("delivery.modeMoto")).toLowerCase() })}</button>
                    )}
                  </div>
                  <div data-testid="no-driver-note" className="mt-3 w-full text-center text-xs font-semibold text-[#B0392B] bg-[#FBE9E7] rounded-xl py-2.5">Aucun chauffeur en ligne à proximité pour le moment.</div>
                </div>
              )}
              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
                <div className="flex items-center justify-between">
                  <h2 className="font-head font-bold text-lg">{isDeliv(mode) ? t("delivery.title") : (mode === "car" || mode === "comfort") ? t("delivery.reqCar") : t("delivery.reqMoto")}</h2>
                  <button data-testid="locate-me-btn" onClick={locateMe} disabled={locating}
                    className="flex items-center gap-1.5 h-9 px-3 rounded-xl bg-[#F26419] text-white text-xs font-semibold disabled:opacity-60">
                    <LocateFixed className="w-4 h-4" /> {locating ? t("pax.locating") : t("pax.locate")}
                  </button>
                </div>

                {gpsDenied && (
                  <div data-testid="gps-denied-banner" className="mt-4 rounded-2xl p-4 bg-[#FEF3C7] border-2 border-[#F59E0B] text-[#92400E]">
                    <div className="font-bold flex items-center gap-2"><MapPin className="w-4 h-4" /> Activez votre position GPS</div>
                    <p className="text-sm mt-1">Pour trouver les chauffeurs proches et fixer votre point de départ exact, votre position est nécessaire. Quand votre téléphone affiche « Autoriser la localisation », appuyez sur <b>Autoriser</b>. Sans GPS, votre départ peut être incorrect et les chauffeurs proches peuvent ne pas vous voir.</p>
                    <button data-testid="gps-retry-btn" onClick={locateMe} disabled={locating} className="mt-2 h-10 px-4 rounded-xl bg-[#F59E0B] hover:bg-[#d98a08] text-white font-semibold text-sm transition-colors disabled:opacity-60">{locating ? "Localisation…" : "Activer ma position maintenant"}</button>
                  </div>
                )}
                <div className="mt-4" data-testid="ride-map">
                  <RideMap center={mapCenter} pickup={pickupCoords} dest={destCoords} drivers={nearby} agents={liveAgents} onMapClick={onMapClick} height={340} />
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-[#75807A] flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-[#D94F2B]" /> {t("pax.tapDest")}
                    </span>
                    <span data-testid="nearby-count" className="font-semibold text-[#D94F2B] flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F26419] animate-pulse" /> {nearby.length} {t(mode === "car" ? "pax.nearbyCar" : "pax.nearby")}{pickupEta != null ? ` · ~${pickupEta} ${t("common.min")}` : ""}
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-xs font-semibold text-[#75807A] mb-2">Villes desservies — départ rapide</div>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="city-chips">
                    {(isEurope ? EU_CITIES : CM_CITIES).map((ci) => (
                      <button key={ci.name} data-testid={`city-${ci.name}`} onClick={() => selectCity(ci)} className={`shrink-0 h-9 px-3.5 rounded-full border text-xs font-semibold transition-colors ${pickup === ci.name ? "bg-[#F26419] border-[#D94F2B] text-white" : "bg-white border-[#E2DFD8] text-[#3B423E] hover:border-[#F26419]"}`}>
                        {ci.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  <AddressAutocomplete testid="ride-pickup" value={pickup} country={region?.country_code || current}
                    onChange={(v) => { setPickup(v); setPickupCoords(null); }} placeholder={t("pax.pickupPh")} dotColor="#F26419"
                    onSelect={(r) => { setPickup(r.label); setPickupCoords({ lat: r.lat, lng: r.lng }); }} />
                  <AddressAutocomplete testid="ride-dest" value={dest} country={region?.country_code || current}
                    onChange={(v) => { setDest(v); setDestCoords(null); }} placeholder={isDeliv(mode) ? t("delivery.dropoffPh") : t("pax.destPh")} dotColor="#D94F2B"
                    onSelect={(r) => { setDest(r.label); setDestCoords({ lat: r.lat, lng: r.lng }); }} />
                  {isDeliv(mode) && (
                    <div className="space-y-4" data-testid="delivery-fields">
                      <div className="relative">
                        <Package className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D94F2B]" />
                        <Input data-testid="delivery-item" value={item} onChange={(e) => setItem(e.target.value)} placeholder={t("delivery.itemPh")} className="h-14 rounded-xl border-2 pl-10" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input data-testid="delivery-recipient-name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder={t("delivery.recipientNamePh")} className="h-14 rounded-xl border-2" />
                        <Input data-testid="delivery-recipient-phone" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder={t("delivery.recipientPhonePh")} className="h-14 rounded-xl border-2" />
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="flex justify-between text-sm font-medium text-[#3B423E]">
                      <span>{t("pax.distance")}{pickupCoords && destCoords ? " · GPS" : ""}</span><span className="font-bold">{distance} km</span>
                    </div>
                    <input data-testid="ride-distance" type="range" min="1" max="40" value={distance} onChange={(e) => setDistance(Number(e.target.value))} className="w-full mt-2 accent-[#D94F2B]" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#3B423E] flex items-center gap-1.5"><Star className="w-4 h-4 text-[#F26419]" /> {t("pax.favorites")}</span>
                      {dest.trim() && (
                        <div className="flex gap-1.5">
                          <button data-testid="save-fav-home" onClick={() => saveFavorite(t("pax.home"))} className="h-8 px-2.5 rounded-lg bg-[#F0EBE1] hover:bg-[#F26419] text-xs font-semibold flex items-center gap-1 transition-colors"><Home className="w-3.5 h-3.5" />{t("pax.home")}</button>
                          <button data-testid="save-fav-work" onClick={() => saveFavorite(t("pax.work"))} className="h-8 px-2.5 rounded-lg bg-[#F0EBE1] hover:bg-[#F26419] text-xs font-semibold flex items-center gap-1 transition-colors"><Briefcase className="w-3.5 h-3.5" />{t("pax.work")}</button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
                      {favorites.length === 0 && <span className="text-xs text-[#75807A]">{t("pax.noFav")}</span>}
                      {favorites.map((f) => {
                        const lab = f.label.toLowerCase();
                        const Icon = lab.includes("mais") || lab === "home" ? Home : lab.includes("bur") || lab === "work" ? Briefcase : MapPin;
                        return (
                          <button key={f.id} data-testid={`fav-${f.id}`} onClick={() => pickFavorite(f)}
                            className="shrink-0 h-9 px-3 rounded-full bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] text-xs font-semibold flex items-center gap-1.5 transition-colors">
                            <Icon className="w-3.5 h-3.5 text-[#D94F2B]" /> {f.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
                <h3 className="font-head font-semibold">{t("pax.payment")}</h3>
                {isEurope ? (
                  <div data-testid="eu-payment-note" className="mt-4 rounded-2xl border-2 border-[#2563EB] bg-[#2563EB]/5 p-4 flex items-center gap-3">
                    <span className="w-11 h-11 shrink-0 rounded-xl bg-[#2563EB] text-white flex items-center justify-center"><Smartphone className="w-5 h-5" /></span>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[#1A1A1A]">Paiement sécurisé par carte</div>
                      <div className="text-xs text-[#75807A]">Vous réglez votre course en € via Stripe au moment de la commande.</div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {PAY.map((p) => (
                      <button key={p.id} data-testid={`pay-${p.id}`} onClick={() => setPayment(p.id)}
                        className={`h-20 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${payment === p.id ? "border-[#1A1A1A] scale-[1.02]" : "border-[#E2DFD8]"}`}>
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: p.color }}>
                          <p.icon className="w-4 h-4" style={{ color: p.txt }} />
                        </span>
                        <span className="text-xs font-semibold">{p.id === "wallet" ? "Portefeuille" : t(`pay.${p.id}`).split(" ")[0]}</span>
                        {p.id === "wallet" && <span className="text-[10px] text-[#75807A]" data-testid="pay-wallet-balance">{fcfa(wallet?.balance || 0)}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {!isEurope && payment === "wallet" && (
                  <div data-testid="wallet-pay-note" className="mt-3 rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-3 text-xs text-[#3B423E] flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-[#1A1A1A] shrink-0" />
                    <span>Solde : <span className="font-semibold text-[#1A1A1A]">{fcfa(wallet?.balance || 0)}</span> · la course est débitée de votre portefeuille à la réservation.</span>
                  </div>
                )}
              </div>

              <div className="bg-[#1A1A1A] rounded-3xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white/60 text-sm">{t("pax.estimated")}</div>
                    <div className="font-head font-black text-3xl" data-testid="ride-estimate">{estimate ? money(estimate.price) : "—"}</div>
                    <div className="text-white/50 text-xs mt-1">{isEurope ? "Europe" : currentCountry?.name} · {distance} km · {(mode === "car" || mode === "comfort") ? (mode === "comfort" ? t("delivery.modeComfort") : t("delivery.modeCar")) : mode === "velo" ? t("delivery.modeVelo") : mode === "delivery" ? t("delivery.badge") : t("delivery.modeMoto")}{isDeliv(mode) ? ` · ${t("delivery.handling")}` : ""}</div>
                    {isEurope && region && (
                      <div className="text-white/50 text-xs mt-1" data-testid="eu-pricing-detail">
                        {mode === "car"
                          ? `Base ${eur(region.car_pricing?.base)} + ${eur(region.car_pricing?.per_km)}/km · min ${eur(region.car_pricing?.min)}`
                          : `Base ${eur(region.delivery_pricing?.base)} + ${eur(region.delivery_pricing?.per_km)}/km · min ${eur(region.delivery_pricing?.min)}`}
                      </div>
                    )}
                    <div className="text-[#F26419] text-xs mt-1.5 flex items-center gap-1.5" data-testid="ride-eta"><Clock className="w-3.5 h-3.5" /> ≈ {tripEta} {t("common.min")} {t("pax.rideDuration")}</div>
                  </div>
                  {!isEurope && referral?.credit > 0 && (
                    <div className="text-right">
                      <div className="text-[#F26419] text-xs font-semibold flex items-center gap-1 justify-end"><Gift className="w-3.5 h-3.5" /> {t("pax.credit")}</div>
                      <div className="font-head font-bold text-lg text-[#F26419]" data-testid="credit-badge">-{fcfa(Math.min(referral.credit, estimate?.price || 0))}</div>
                    </div>
                  )}
                </div>
                <Button data-testid="ride-book-btn" onClick={book} disabled={loading || noDriver} className="mt-5 w-full h-16 rounded-2xl bg-[#F26419] hover:bg-[#D85312] text-[#1A1A1A] font-black text-lg flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
                  <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                    {mode === "velo" ? <Bike className="w-5 h-5 text-[#16a34a]" /> : mode === "delivery" ? <Package className="w-5 h-5 text-[#D94F2B]" /> : (mode === "car" || mode === "comfort") ? <Car className="w-5 h-5 text-[#2563EB]" /> : <Bike className="w-5 h-5 text-[#F26419]" />}
                  </span>
                  {loading ? "…" : noDriver ? "Aucun chauffeur disponible" : isEurope ? `Payer ${estimate ? money(estimate.price) : ""} et commander` : (isDeliv(mode) ? t("delivery.send") : (mode === "car" || mode === "comfort") ? t("delivery.bookCar") : t("delivery.bookMoto"))}
                </Button>
              </div>
              </>)}
            </motion.div>
          )}

          {tab === "history" && (
            <motion.div key="history" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6 space-y-3">
              <h2 className="font-head font-bold text-lg">{t("pax.history")}</h2>
              {rides.length === 0 && <p className="text-[#75807A] py-10 text-center">{t("pax.noRides")}</p>}
              {rides.filter((r) => r.status !== "pending_payment").map((r) => (
                <div key={r.id} data-testid={`ride-card-${r.id}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <span data-testid={`type-badge-${r.id}`} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-1.5 ${r.type === "delivery" ? "bg-[#F26419]/15 text-[#D94F2B]" : r.vehicle_type === "car" ? "bg-[#2563EB]/15 text-[#2563EB]" : "bg-[#F26419]/25 text-[#8a6d00]"}`}>{r.type === "delivery" ? <><Package className="w-3 h-3" /> {t("delivery.badge")}</> : r.vehicle_type === "car" ? <><Car className="w-3 h-3" /> {t("delivery.badgeCar")}</> : <><Bike className="w-3 h-3" /> {t("delivery.badgeMoto")}</>}</span>
                      <div className="flex items-center gap-2 text-sm"><span className="w-2 h-2 rounded-full bg-[#F26419]" />{r.pickup}</div>
                      <div className="flex items-center gap-2 text-sm mt-1"><MapPin className="w-3 h-3 text-[#D94F2B]" />{r.destination}</div>
                      {r.type === "delivery" && (
                        <div className="mt-1.5 text-xs text-[#3B423E]" data-testid={`delivery-info-${r.id}`}><Package className="w-3 h-3 inline mr-1 text-[#D94F2B]" />{r.item_description || "—"} · {r.recipient_name || ""} {r.recipient_phone || ""}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-head font-bold">{rideMoney(r)}</div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${statusColor[r.status]}20`, color: statusColor[r.status] }}>
                        {t(`rideStatus.${r.status}`)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-[#75807A]">
                    <span className="flex items-center gap-1.5 flex-wrap">
                      {t(`pay.${r.payment_method}`)}{r.driver_name ? ` · ${r.driver_name}` : ""}
                      {r.status === "completed" && (r.cash_payment || r.payment_method === "cash" || r.payment_method_final === "cash") && (
                        <span data-testid={`cash-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#16a34a]/15 text-[#16a34a] px-2 py-0.5 text-[10px] font-bold">Payé en espèces ✓</span>
                      )}
                    </span>
                    {r.status === "completed" && r.rating && (
                      <div className="flex items-center gap-1" data-testid={`rated-${r.id}`}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-4 h-4 ${r.rating >= s ? "fill-[#F26419] text-[#F26419]" : "text-[#E2DFD8]"}`} />
                        ))}
                      </div>
                    )}
                  </div>

                  {r.status === "accepted" && (
                    <div data-testid={`tracking-${r.id}`} className="mt-3 rounded-2xl bg-[#F0EBE1] p-4">
                      <div className="text-xs uppercase tracking-wide text-[#D94F2B] font-semibold flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#F26419] animate-pulse" /> {t("pax.tracking")}
                      </div>
                      {r.payment_pending && (
                        <div className="mt-3 rounded-2xl bg-[#1A1A1A] text-white p-4" data-testid={`client-payment-panel-${r.id}`}>
                          <div className="text-center text-[11px] font-bold uppercase tracking-wide text-[#F26419] bg-[#F26419]/15 rounded-lg py-1.5 mb-3">{t("pay.freeBanner")}</div>
                          <div className="text-center">
                            <div className="text-xs text-white/60">{t("pay.amountToPay")}</div>
                            <div className="font-head font-black text-3xl">{fcfa(r.price)}</div>
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <button data-testid={`client-pay-cash-${r.id}`} onClick={() => { setPayMethod("cash"); setDriverQr(null); }} className={`h-14 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 ${payMethod === "cash" ? "border-[#F26419] bg-[#F26419]/15 text-[#F26419]" : "border-white/20 text-white"}`}><Banknote className="w-5 h-5" /> {t("pay.payCash")}</button>
                            <button data-testid={`client-pay-mobile-${r.id}`} onClick={() => showMobileQr(r.id)} className={`h-14 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 ${payMethod === "mobile" ? "border-[#F26419] bg-[#F26419]/15 text-[#F26419]" : "border-white/20 text-white"}`}><Smartphone className="w-5 h-5" /> {t("pay.payMobile")}</button>
                          </div>
                          {payMethod === "mobile" && driverQr && (
                            <div className="mt-3 text-center" data-testid={`client-qr-${r.id}`}>
                              {(driverQr.active === "mtn" ? driverQr.mtn : driverQr.orange) ? (
                                <>
                                  <img src={driverQr.active === "mtn" ? driverQr.mtn : driverQr.orange} alt="QR" className="w-44 h-44 object-contain mx-auto rounded-xl bg-white p-1" />
                                  <div className="mt-1 text-xs text-white/70">{t("pay.scanToPay")} · {driverQr.active === "mtn" ? "MTN MoMo" : "Orange Money"}</div>
                                  {(driverQr.active === "mtn" ? driverQr.mtn_num : driverQr.orange_num) && <div className="text-base font-black text-[#F26419] mt-0.5" data-testid={`client-qr-num-${r.id}`}>{driverQr.active === "mtn" ? driverQr.mtn_num : driverQr.orange_num}</div>}
                                  {(() => {
                                    const op = driverQr.active;
                                    const num = op === "mtn" ? driverQr.mtn_num : driverQr.orange_num;
                                    const u = buildUssd(op, num, r.price);
                                    if (u) return (<a data-testid={`client-ussd-${r.id}`} href={`tel:${u.replace(/\*/g, "%2A").replace(/#/g, "%23")}`} className="mt-3 w-full h-12 rounded-xl bg-[#F26419] text-black font-bold flex items-center justify-center gap-2"><Phone className="w-5 h-5" /> {t("pay.dialToPay")} · {fcfa(r.price)}</a>);
                                    if (op === "orange" && num) return (<div className="mt-3 text-xs text-white/80" data-testid={`client-om-note-${r.id}`}>{t("pay.omNote", { amount: fcfa(r.price), num })}</div>);
                                    return null;
                                  })()}
                                </>
                              ) : (
                                <div className="text-sm text-[#F26419]">{t("pay.noQrClient")}</div>
                              )}
                            </div>
                          )}
                          {r.client_marked_paid ? (
                            <div className="mt-4 text-center text-sm font-semibold text-[#16a34a]" data-testid={`client-paid-done-${r.id}`}>{t("pay.paidWaiting")}</div>
                          ) : (
                            <button data-testid={`client-ipaid-${r.id}`} disabled={!payMethod} onClick={() => iPaid(r.id, payMethod)} className="mt-4 w-full h-14 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold disabled:opacity-40">{t("pay.iPaid")}</button>
                          )}
                        </div>
                      )}
                      {r.pickup_lat != null && r.dest_lat != null && (
                        <a data-testid={`gmaps-passenger-${r.id}`} href={googleMapsDirUrl([r.driver_cur_lat != null ? { lat: r.driver_cur_lat, lng: r.driver_cur_lng } : null, { lat: r.pickup_lat, lng: r.pickup_lng }, { lat: r.dest_lat, lng: r.dest_lng }])} target="_blank" rel="noreferrer" className="mt-3 w-full h-12 rounded-xl bg-[#1A73E8] hover:bg-[#1666d0] text-white font-bold flex items-center justify-center gap-2">🧭 Suivre dans Google Maps</a>
                      )}
                      {r.pickup_lat != null && r.dest_lat != null && (
                        <div className="mt-3">
                          {r.driver_cur_lat != null && (
                            <div data-testid={`eta-${r.id}`} className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[#1A1A1A] text-white px-3 py-2">
                              <span className="text-xs font-semibold flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#F26419] animate-pulse" /> {r.driver_eta_min != null && (r.driver_dist_km == null || r.driver_dist_km > 0.35) ? `Votre chauffeur arrive · ~${r.driver_eta_min} min` : "🛵 Votre chauffeur est tout proche !"}</span>
                              {r.driver_dist_km != null && <span className="text-[11px] text-white/60">{r.driver_dist_km < 1 ? `${Math.round(r.driver_dist_km * 1000)} m` : `${r.driver_dist_km} km`}</span>}
                            </div>
                          )}
                          <RideMap
                            center={{ lat: r.pickup_lat, lng: r.pickup_lng }}
                            pickup={{ lat: r.pickup_lat, lng: r.pickup_lng }}
                            dest={{ lat: r.dest_lat, lng: r.dest_lng }}
                            driver={r.driver_cur_lat != null ? { lat: r.driver_cur_lat, lng: r.driver_cur_lng } : undefined}
                            height={180}
                          />
                        </div>
                      )}
                      {r.arrived_at && (
                        <div data-testid={`arrival-verify-${r.id}`} className="mt-3 rounded-2xl bg-[#16a34a]/10 border border-[#16a34a]/40 p-3 flex items-center gap-3">
                          {r.driver_photo ? (
                            <img src={r.driver_photo} alt={r.driver_name} data-testid={`arrival-selfie-${r.id}`} className="w-16 h-16 rounded-xl object-cover border-2 border-[#16a34a]" />
                          ) : (
                            <span className="w-16 h-16 rounded-xl bg-[#16a34a] text-white flex items-center justify-center font-head font-bold text-2xl">{(r.driver_name || "C")[0].toUpperCase()}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-head font-bold text-[#16a34a] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Vérifiez votre chauffeur</div>
                            <p className="text-xs text-[#3B423E] mt-0.5">Avant de monter, comparez ce selfie et la plaque avec le chauffeur présent.</p>
                            <span data-testid={`arrival-plate-${r.id}`} className="mt-1 inline-block font-mono font-bold text-[#1A1A1A] bg-[#F26419] px-2 py-0.5 rounded-md tracking-wider text-sm">{r.driver_plate || "—"}</span>
                          </div>
                        </div>
                      )}
                      <div className="mt-3 rounded-2xl bg-white border border-[#E2DFD8] p-3">
                        <div className="flex items-center gap-3">
                          {r.driver_photo ? (
                            <img src={r.driver_photo} alt={r.driver_name} data-testid={`driver-photo-${r.id}`} className="w-14 h-14 rounded-2xl object-cover" />
                          ) : (
                            <span className="w-14 h-14 rounded-2xl bg-[#F26419] text-white flex items-center justify-center font-head font-bold text-xl">{(r.driver_name || "C")[0].toUpperCase()}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-head font-bold text-[#1A1A1A] truncate">{r.driver_name || t("hero.driver")}</span>                              {r.driver_rating ? <span className="flex items-center gap-0.5 text-xs text-[#75807A]"><Star className="w-3 h-3 fill-[#F26419] text-[#F26419]" />{Number(r.driver_rating).toFixed(1)}</span> : null}
                              {r.driver_rank && r.driver_rank !== "debutant" && (
                                <span data-testid={`driver-rank-${r.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${r.driver_rank === "expert" ? "bg-[#F26419] text-white" : "bg-[#1A1A1A] text-white"}`}>{r.driver_rank === "expert" ? "🏆 Expert" : "⭐ Pro"}</span>
                              )}
                            </div>
                            <div className="text-xs text-[#008a3a] flex items-center gap-1 mt-0.5"><ShieldCheck className="w-3.5 h-3.5 text-[#D94F2B]" /> {t("pax.verified")}</div>
                            <div className="mt-1 flex items-center gap-2 text-xs">
                              <span className="text-[#75807A]" data-testid={`driver-vehicle-${r.id}`}><Bike className="w-3.5 h-3.5 inline mr-1 text-[#1A1A1A]" />{r.driver_vehicle || "—"}</span>
                              <span data-testid={`driver-plate-${r.id}`} className="font-mono font-bold text-[#1A1A1A] bg-[#F26419] px-2 py-0.5 rounded-md tracking-wider">{r.driver_plate || "—"}</span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <CallButton testId={`call-driver-${r.id}`} targetUserId={r.driver_id} name={r.driver_name || t("hero.driver")} label={t("pax.call")} className="h-11 rounded-xl bg-[#008a3a] hover:opacity-90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity" />
                          <a data-testid={`whatsapp-driver-${r.id}`} href={waLink(r.driver_phone, t("common.waHello"))} target="_blank" rel="noopener noreferrer" className="h-11 rounded-xl bg-[#25D366] hover:opacity-90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity" title={t("common.whatsapp")}>
                            <MessageCircle className="w-4 h-4" /> {t("common.whatsapp")}
                          </a>
                          <button data-testid={`chat-open-${r.id}`} onClick={() => setChatRide(r)} className="h-11 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                            <MessageCircle className="w-4 h-4" /> {t("chat.title")}
                          </button>
                        </div>
                        <div className="mt-2 text-[11px] text-[#75807A] text-center" data-testid={`driver-phone-${r.id}`}>{r.driver_phone}</div>
                      </div>
                      <button data-testid={`share-trip-${r.id}`} onClick={() => shareTrip(r)} className="mt-3 w-full h-11 rounded-xl bg-[#25D366] hover:opacity-90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity">
                        <ShieldCheck className="w-4 h-4" /> Partager mon trajet
                      </button>
                      {!r.payment_pending && (
                        <button data-testid={`cancel-ride-${r.id}`} onClick={() => cancelRide(r.id)} className="mt-3 w-full h-11 rounded-xl border-2 border-[#D32F2F] text-[#D32F2F] font-semibold text-sm hover:bg-[#D32F2F] hover:text-white transition-colors">{t("pax.cancel")}</button>
                      )}
                    </div>
                  )}

                  {r.status === "requested" && (
                    <div className="mt-3 space-y-2">
                      <button
                        data-testid={`chat-open-${r.id}`}
                        onClick={() => setChatRide(r)}
                        className="w-full h-11 rounded-xl bg-[#F0EBE1] hover:bg-[#F26419] hover:text-white text-[#3B423E] text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" /> {t("chat.withDriver")}
                      </button>
                      <button data-testid={`share-trip-${r.id}`} onClick={() => shareTrip(r)} className="w-full h-11 rounded-xl bg-[#25D366] hover:opacity-90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity">
                        <ShieldCheck className="w-4 h-4" /> Partager mon trajet
                      </button>
                      <button data-testid={`cancel-ride-${r.id}`} onClick={() => cancelRide(r.id)} className="w-full h-11 rounded-xl border-2 border-[#D32F2F] text-[#D32F2F] font-semibold text-sm hover:bg-[#D32F2F] hover:text-white transition-colors">{t("pax.cancel")}</button>
                    </div>
                  )}

                  {r.status === "completed" && (
                    <button
                      data-testid={`receipt-${r.id}`}
                      onClick={() => downloadReceipt(r)}
                      className="mt-3 w-full h-11 rounded-xl bg-[#F0EBE1] hover:bg-[#F26419] text-[#1A1A1A] text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Wallet className="w-4 h-4" /> {t("pax.receipt")}
                    </button>
                  )}
                  {r.status === "completed" && !r.rating && (
                    <button
                      data-testid={`rate-open-${r.id}`}
                      onClick={() => setRatingRide(r)}
                      className="mt-3 w-full h-11 rounded-xl bg-[#1A1A1A] hover:bg-black text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Star className="w-4 h-4" /> {t("rating.rate")}
                    </button>
                  )}
                </div>
              ))}
            </motion.div>
          )}

          {tab === "profile" && (
            <motion.div key="profile" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-7">
                <div className="w-16 h-16 rounded-2xl bg-[#F26419] flex items-center justify-center text-white font-head font-black text-2xl">
                  {(user?.name || "P")[0].toUpperCase()}
                </div>
                <h2 className="mt-4 font-head font-bold text-xl">{user?.name || t("hero.passenger")}</h2>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between border-b border-[#E2DFD8] pb-2"><span className="text-[#75807A]">{t("pax.phone")}</span><span className="font-medium">{user?.phone}</span></div>
                  <div className="flex justify-between border-b border-[#E2DFD8] pb-2"><span className="text-[#75807A]">{t("common.country")}</span><span className="font-medium">{currentCountry?.name}</span></div>
                  <div className="flex justify-between"><span className="text-[#75807A]">Rides</span><span className="font-medium">{rides.length}</span></div>
                </div>
              </div>

              {pushSupported() && (
                <button
                  data-testid="enable-notif-btn"
                  onClick={enableNotif}
                  disabled={notifOn}
                  className={`mt-4 w-full rounded-2xl p-4 flex items-center gap-3 text-left border ${notifOn ? "bg-[#F0EBE1] border-[#E2DFD8]" : "bg-white border-[#E2DFD8] hover:border-[#D94F2B]"}`}
                >
                  <span className="w-11 h-11 rounded-xl bg-[#F26419] text-white flex items-center justify-center"><Bell className="w-5 h-5" /></span>
                  <div className="flex-1">
                    <div className="font-head font-bold text-[#1A1A1A]">{notifOn ? t("notif.enabled") : t("notif.enable")}</div>
                    <div className="text-xs text-[#75807A]">{t("notif.desc")}</div>
                  </div>
                </button>
              )}

              <div data-testid="identity-verify-card" className="mt-4 rounded-3xl p-6 bg-white border border-[#E2DFD8]">
                <div className="flex items-center gap-2 font-head font-black text-xl text-[#1A1A1A]"><ShieldCheck className="w-6 h-6 text-[#16a34a]" /> Vérification d'identité</div>
                {user?.profile_complete ? (
                  <div data-testid="identity-verified" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#16a34a] bg-[#16a34a]/10 rounded-full px-3 py-1"><Check className="w-4 h-4" /> Identité vérifiée</div>
                ) : (
                  <>
                    <p className="mt-1 text-xs text-[#75807A]">Facultatif — vous pouvez commander vos courses sans la faire. Vérifiez votre identité (selfie + CNI) quand vous le souhaitez pour plus de confiance.</p>
                    <button data-testid="verify-identity-btn" onClick={() => setVerifyMode(true)} className="mt-4 w-full h-12 rounded-xl bg-[#16a34a] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" /> Vérifier mon identité</button>
                  </>
                )}
              </div>

              <div data-testid="become-partner-card" className="mt-4 rounded-3xl p-6 bg-white border border-[#E2DFD8]">
                <div className="flex items-center gap-2 font-head font-black text-xl text-[#1A1A1A]"><ShieldCheck className="w-6 h-6 text-[#F26419]" /> Devenir partenaire</div>
                {(user?.roles || []).includes("driver") ? (
                  <>
                    <p className="mt-1 text-xs text-[#75807A]">Votre profil chauffeur est activé. Basculez à tout moment vers votre tableau de bord chauffeur.</p>
                    <button data-testid="switch-to-driver" onClick={goDriver} disabled={!!roleLoading} className="mt-4 w-full h-12 rounded-xl bg-[#1A1A1A] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2"><Bike className="w-4 h-4" /> Passer en mode Chauffeur</button>
                  </>
                ) : (
                  <>
                    <p className="mt-1 text-xs text-[#75807A]">Gagnez de l'argent avec Mboa Go. Activez un profil, envoyez vos documents (CNI, Permis, Carte grise) et commencez dès validation par l'administrateur.</p>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <button data-testid="become-driver" onClick={() => becomeDriver("ride")} disabled={!!roleLoading} className="h-14 rounded-xl bg-[#F26419] hover:opacity-90 text-white font-bold inline-flex flex-col items-center justify-center leading-tight"><span className="inline-flex items-center gap-1"><Bike className="w-4 h-4" /> Devenir Chauffeur</span><span className="text-[10px] opacity-80 font-medium">Moto · Voiture</span></button>
                      <button data-testid="become-courier" onClick={() => becomeDriver("delivery")} disabled={!!roleLoading} className="h-14 rounded-xl bg-[#16a34a] hover:opacity-90 text-white font-bold inline-flex flex-col items-center justify-center leading-tight"><span className="inline-flex items-center gap-1"><Package className="w-4 h-4" /> Devenir Livreur</span><span className="text-[10px] opacity-80 font-medium">Vélo · Moto</span></button>
                    </div>
                  </>
                )}
              </div>

              {!isEurope && (
              <div data-testid="passenger-wallet-card" className="mt-4 rounded-3xl p-6 bg-white border border-[#E2DFD8]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-head font-black text-xl text-[#1A1A1A]"><Wallet className="w-6 h-6 text-[#F26419]" /> Portefeuille</div>
                  <div data-testid="passenger-wallet-balance" className="font-head font-black text-2xl text-[#1A1A1A]">{(wallet?.balance ?? 0).toLocaleString("fr-FR")} FCFA</div>
                </div>
                <p className="mt-1 text-xs text-[#75807A]">Rechargez via Orange Money ou MTN MoMo, puis payez vos courses avec votre solde. <span className="font-semibold text-[#D94F2B]">Chaque recharge est validée manuellement par l'administrateur</span> après vérification du paiement.</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button data-testid="recharge-orange" onClick={() => openRecharge("orange_money")} className="h-12 rounded-xl bg-[#FF6600] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> Orange Money</button>
                  <button data-testid="recharge-mtn" onClick={() => openRecharge("mtn_momo")} className="h-12 rounded-xl bg-[#FFCC00] hover:opacity-90 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> MTN MoMo Pay</button>
                </div>
                {!isEurope && activation?.active && !activation?.free && (
                  <button data-testid="pax-activation-receipt-btn" onClick={async () => { try { const r = await api.get('/passenger/subscription/receipt.pdf', { responseType: 'blob' }); const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = 'recu-activation.pdf'; a.click(); URL.revokeObjectURL(url); } catch (e) { toast.error("Aucun reçu disponible pour le moment."); } }} className="mt-3 w-full h-10 rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold hover:bg-[#e8e1d3] transition-colors">📄 Mon reçu d'activation (PDF)</button>
                )}
                {wallet?.transactions?.length ? (
                  <div className="mt-4 space-y-1.5" data-testid="wallet-transactions">
                    {wallet.transactions.slice(0, 5).map((t2) => {
                      const st = t2.status || "approved";
                      const isRecharge = t2.type === "recharge";
                      const sColor = st === "pending" ? "text-[#B8860B]" : st === "rejected" ? "text-[#D32F2F]" : "text-[#16a34a]";
                      const sLabel = st === "pending" ? "En attente de validation" : st === "rejected" ? "Refusée" : "Validée";
                      return (
                        <div key={t2.id} className="flex items-center justify-between gap-2 text-xs text-[#3B423E]">
                          <span>{t2.method === "mtn_momo" ? "MTN MoMo" : "Orange Money"} · {isRecharge ? "recharge" : t2.type}{isRecharge && <span className={`ml-1 font-semibold ${sColor}`}>· {sLabel}</span>}{t2.receipt_no && <span className="ml-1 text-[10px] text-[#75807A]">· Reçu #{t2.receipt_no}</span>}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`font-semibold ${st === "rejected" ? "text-[#D32F2F] line-through" : st === "pending" ? "text-[#B8860B]" : "text-[#16a34a]"}`}>+{(t2.amount || 0).toLocaleString("fr-FR")} FCFA</span>
                            {isRecharge && st === "approved" && (
                              <button data-testid={`recharge-receipt-${t2.id}`} onClick={() => openRechargeReceipt(t2)} className="text-[11px] font-bold text-[#D94F2B] underline whitespace-nowrap">Reçu</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              )}

              {rechargeModal && (
                <div data-testid="recharge-modal" className="fixed inset-0 z-[2000] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setRechargeModal(null)}>
                  <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                    <h3 className="font-head font-black text-xl text-[#1A1A1A]">Recharger via {rechargeModal === "mtn_momo" ? "MTN MoMo" : "Orange Money"}</h3>
                    <div className="mt-4 rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-4">
                      <div className="text-xs text-[#75807A]">1. Envoyez le montant à ce numéro marchand officiel 🔒 :</div>
                      <div className="mt-1 flex items-center justify-between">
                        <span data-testid="merchant-number" className="font-head font-black text-2xl text-[#D94F2B] tracking-wide">
                          {(rechargeModal === "mtn_momo" ? rechargeInfo?.mtn_number : rechargeInfo?.orange_number) || "Numéro non configuré"}
                        </span>
                        {(rechargeModal === "mtn_momo" ? rechargeInfo?.mtn_number : rechargeInfo?.orange_number) && (
                          <button data-testid="copy-merchant" onClick={() => { navigator.clipboard?.writeText(rechargeModal === "mtn_momo" ? rechargeInfo.mtn_number : rechargeInfo.orange_number); toast.success("Numéro copié"); }} className="text-xs font-semibold text-[#D94F2B] underline">Copier</button>
                        )}
                      </div>
                      <div className="mt-1 text-[11px] text-[#75807A]">{rechargeInfo?.merchant_name || "Mboa Go"}</div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="text-sm font-medium text-[#3B423E]">2. Montant versé (FCFA)</label>
                        <Input data-testid="rc-amount" type="number" inputMode="numeric" value={rcAmount} onChange={(e) => setRcAmount(e.target.value)} placeholder="Ex : 1000" className="mt-1.5 h-12 rounded-xl border-2" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#3B423E]">3. ID de transaction (reçu par SMS)</label>
                        <Input data-testid="rc-txid" value={rcTxId} onChange={(e) => setRcTxId(e.target.value)} placeholder="Ex : MP240612.1234.A56789" className="mt-1.5 h-12 rounded-xl border-2" />
                        <p className="mt-1 text-[11px] text-[#75807A]">Copiez l'identifiant depuis le SMS de confirmation Mobile Money.</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#3B423E]">4. Reçu Mobile Money (capture d'écran, obligatoire)</label>
                        <label data-testid="rc-proof-label" className="mt-1.5 flex items-center gap-3 rounded-xl border-2 border-dashed border-[#E2DFD8] p-3 cursor-pointer hover:border-[#D94F2B] transition-colors">
                          {rcProofPreview ? (
                            <img src={rcProofPreview} alt="reçu" className="w-12 h-12 object-cover rounded-lg" />
                          ) : (
                            <span className="w-12 h-12 rounded-lg bg-[#FDF8F0] flex items-center justify-center"><Smartphone className="w-5 h-5 text-[#75807A]" /></span>
                          )}
                          <span className="text-sm text-[#75807A]">{rcProofUploading ? "Envoi…" : rcProofPath ? "Reçu joint ✓ — changer" : "Joindre la capture pour accélérer la validation"}</span>
                          <input data-testid="rc-proof-input" type="file" accept="image/*" onChange={uploadProof} className="hidden" />
                        </label>
                      </div>
                      <Button data-testid="rc-submit" onClick={submitRecharge} disabled={rcLoading || rcProofUploading} className="w-full h-12 rounded-xl bg-[#D94F2B] hover:-translate-y-0.5 transition-transform text-white font-bold">
                        {rcLoading ? "Envoi…" : "Envoyer pour validation"}
                      </Button>
                      <button data-testid="rc-cancel" onClick={() => setRechargeModal(null)} className="w-full text-sm text-[#75807A] hover:text-[#1A1A1A]">Annuler</button>
                      <p className="text-[11px] text-[#75807A] text-center">Votre solde sera crédité après vérification par l'administrateur.</p>
                    </div>
                  </div>
                </div>
              )}

              {!isEurope && referral && (
                <div data-testid="referral-card" className="mt-4 rounded-3xl p-6 bg-gradient-to-br from-[#F26419] to-[#D94F2B] text-[#1A1A1A]">
                  <div className="flex items-center gap-2 font-head font-black text-xl"><Gift className="w-6 h-6" /> {t("referral.title")}</div>
                  <p className="mt-1 text-sm text-[#1A1A1A]/80">{t("referral.subtitle")}</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-white/25 rounded-2xl p-3">
                      <div className="text-xs text-[#1A1A1A]/70">{t("referral.credit")}</div>
                      <div className="font-head font-black text-lg" data-testid="referral-credit">{fcfa(referral.credit)}</div>
                    </div>
                    <div className="bg-white/25 rounded-2xl p-3">
                      <div className="text-xs text-[#1A1A1A]/70">{t("referral.invited")}</div>
                      <div className="font-head font-black text-lg">{referral.referrals_count}</div>
                    </div>
                  </div>
                  <div className="mt-4 bg-white rounded-2xl p-3">
                    <div className="text-[10px] uppercase tracking-wide text-[#75807A]">{t("referral.yourCode")}</div>
                    <div className="font-head font-black text-xl tracking-widest truncate" data-testid="referral-code">{referral.code}</div>
                    <button data-testid="referral-copy" onClick={copyReferral} className="mt-3 w-full h-11 rounded-xl bg-[#1A1A1A] text-white font-semibold text-sm flex items-center justify-center gap-2">
                      <Copy className="w-4 h-4" /> {t("referral.share")}
                    </button>
                    <button data-testid="referral-whatsapp" onClick={shareReferral} className="mt-2 w-full h-11 rounded-xl bg-[#25D366] hover:opacity-90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity">
                      <MessageCircle className="w-4 h-4" /> Partager sur WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>

      {/* Bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white/90 backdrop-blur-xl border-t border-[#E2DFD8] z-50">
        <div className="max-w-2xl mx-auto grid grid-cols-3">
          {tabs.map((tb) => (
            <button key={tb.id} data-testid={`nav-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`py-3 flex flex-col items-center gap-1 ${tab === tb.id ? "text-[#D94F2B]" : "text-[#75807A]"}`}>
              <tb.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{tb.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {chatRide && (
        <ChatSheet rideId={chatRide.id} role="passenger" peerName={chatRide.driver_name || t("hero.driver")} onClose={() => setChatRide(null)} />
      )}
      <AnimatePresence>
        {ratingRide && (
          <RatingSheet ride={ratingRide} onClose={() => setRatingRide(null)} onRated={loadRides} />
        )}
      </AnimatePresence>
      {isSuperAdmin && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[4000] bg-white rounded-full shadow-xl border border-[#E2DFD8] px-3 py-2" data-testid="admin-zone-bar-passenger">
          <AdminZoneSwitch />
        </div>
      )}
      {receiptRide && (
        <div className="fixed inset-0 z-[3000] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="receipt-sheet" onClick={() => setReceiptRide(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[#16a34a]/10 text-[#16a34a] flex items-center justify-center"><Check className="w-7 h-7" /></div>
              <h3 className="mt-3 font-head font-black text-xl">{t("pax.receiptTitle")}</h3>
              <div className="font-head font-black text-3xl mt-1">{rideMoney(receiptRide)}</div>
              <div className="text-xs text-[#75807A]">{payLabelFr(receiptRide.payment_method_final || receiptRide.payment_method)}</div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-3"><span className="text-[#75807A] shrink-0">{t("pax.from")}</span><span className="font-medium text-right truncate">{receiptRide.pickup || "-"}</span></div>
              <div className="flex justify-between gap-3"><span className="text-[#75807A] shrink-0">{t("pax.to")}</span><span className="font-medium text-right truncate">{receiptRide.destination || "-"}</span></div>
              <div className="flex justify-between gap-3"><span className="text-[#75807A] shrink-0">{t("hero.driver")}</span><span className="font-medium text-right truncate">{receiptRide.driver_name || "-"}</span></div>
              {receiptRide.driver_plate && <div className="flex justify-between gap-3"><span className="text-[#75807A] shrink-0">{t("pax.plate")}</span><span className="font-medium font-mono">{receiptRide.driver_plate}</span></div>}
              <div className="flex justify-between gap-3"><span className="text-[#75807A] shrink-0">Réf.</span><span className="font-mono">{receiptRide.id.slice(0, 8).toUpperCase()}</span></div>
            </div>
            <div className="mt-3 text-center text-[11px] font-bold uppercase tracking-wide text-[#16a34a] bg-[#16a34a]/10 rounded-lg py-1.5">{t("pay.freeBanner")}</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button data-testid="receipt-download-btn" onClick={() => downloadReceipt(receiptRide)} className="h-12 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold flex items-center justify-center gap-2"><Wallet className="w-4 h-4" /> Ouvrir / Imprimer</button>
              <button data-testid="receipt-close-btn" onClick={() => setReceiptRide(null)} className="h-12 rounded-xl bg-[#F0EBE1] hover:bg-[#E2DFD8] text-[#3B423E] font-bold">{t("pax.close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
